/**
 * ============================================================================
 * DROPDOWN COMPONENT - 100% Ready
 * ============================================================================
 */

import React, { useState, useRef, useEffect } from 'react';

/**
 * Dropdown Component
 * @param {Object} props
 * @param {React.ReactNode|function(boolean):React.ReactNode} props.trigger - Trigger element or (isOpen) => element (for aria-expanded)
 * @param {Array} props.items - Dropdown items [{ label, onClick, icon, divider, disabled, danger }]
 * @param {string} props.position - Position (bottom-left, bottom-right, top-left, top-right)
 * @param {boolean} props.closeOnClick - Close dropdown on item click
 * @param {string} props.variant - 'default' | 'sleek' (sleek = dark/glass for header)
 * @param {string} props.className - Additional classes
 * @returns {JSX.Element}
 */
const Dropdown = ({
  trigger,
  items = [],
  position = 'bottom-left',
  closeOnClick = true,
  variant = 'default',
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleEscape = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const positionClasses = {
    'bottom-left': 'top-full left-0 mt-2',
    'bottom-right': 'top-full right-0 mt-2',
    'top-left': 'bottom-full left-0 mb-2',
    'top-right': 'bottom-full right-0 mb-2',
  };

  const handleItemClick = (item) => {
    if (item.disabled) return;
    if (item.onClick) item.onClick();
    if (closeOnClick) setIsOpen(false);
  };

  const triggerNode = typeof trigger === 'function' ? trigger(isOpen) : trigger;
  const isSleek = variant === 'sleek';
  const menuClass = isSleek ? 'dropdown-menu-sleek' : '';

  return (
    <div className={`relative inline-block ${className}`}>
      {/* Trigger */}
      <div
        ref={triggerRef}
        onClick={() => setIsOpen(!isOpen)}
        className="cursor-pointer"
      >
        {triggerNode}
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className={`
            absolute z-50
            ${positionClasses[position] || positionClasses['bottom-left']}
            min-w-[200px]
            rounded-xl shadow-xl py-2 animate-scaleIn
            ${isSleek ? menuClass : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'}
          `}
        >
          {items.map((item, index) => {
            if (item.divider) {
              return (
                <div
                  key={`divider-${index}`}
                  className={isSleek ? 'dropdown-menu-sleek-divider' : 'my-2 border-t border-gray-200 dark:border-gray-700'}
                />
              );
            }

            return (
              <button
                key={index}
                onClick={() => handleItemClick(item)}
                disabled={item.disabled}
                className={isSleek ? `dropdown-menu-sleek-item ${item.danger ? 'dropdown-menu-sleek-item--danger' : ''}` : `
                  w-full px-4 py-2.5
                  text-left text-sm
                  flex items-center gap-3
                  transition-colors duration-200
                  ${
                    item.disabled
                      ? 'opacity-50 cursor-not-allowed text-gray-400'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }
                  ${item.danger ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20' : ''}
                `}
              >
                {item.icon && <span className="text-lg">{item.icon}</span>}
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Dropdown;
